import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getThresholds, type AnomalyThreshold } from "@/services/securityRecords";
import ThresholdEditModal from "./ThresholdEditModal";

interface ThresholdCard {
  metricType: "frequency" | "duplicate";
  title: string;
  icon: string;
  timeWindowMinutes: number;
  countThreshold: number;
}

export default function AnomalyThresholdPanel() {
  const { t } = useTranslation();
  const [thresholds, setThresholds] = useState<AnomalyThreshold[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingMetric, setEditingMetric] = useState<AnomalyThreshold | null>(null);

  const fetchThresholds = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getThresholds();
      setThresholds(data);
    } catch {
      setThresholds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchThresholds();
  }, [fetchThresholds]);

  const cardMap: Record<string, { title: string; icon: string }> = {
    frequency: { title: t("anomaly.threshold.frequencyTitle"), icon: "ri-flashlight-line" },
    duplicate: { title: t("anomaly.threshold.duplicateTitle"), icon: "ri-restart-line" },
  };

  const formatCard = (cfg: AnomalyThreshold): ThresholdCard => ({
    metricType: cfg.metricType,
    title: cardMap[cfg.metricType]?.title ?? cfg.metricType,
    icon: cardMap[cfg.metricType]?.icon ?? "ri-settings-line",
    timeWindowMinutes: cfg.timeWindowMinutes,
    countThreshold: cfg.countThreshold,
  });

  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
        {loading
          ? Array.from({ length: 2 }).map((_, i) => (
              <div
                key={i}
                className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 animate-pulse"
              >
                <div className="h-3 bg-gray-100 rounded w-20 mb-3" />
                <div className="h-5 bg-gray-100 rounded w-32 mb-2" />
                <div className="h-3 bg-gray-100 rounded w-24" />
              </div>
            ))
          : thresholds.map((cfg) => {
              const card = formatCard(cfg);
              return (
                <div
                  key={cfg.metricType}
                  className="rounded-xl border border-gray-100 bg-white px-4 py-3.5 hover:border-gray-200 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center text-gray-500">
                        <i className={`${card.icon} text-sm`}></i>
                      </span>
                      <span className="text-xs text-gray-500">{card.title}</span>
                    </div>
                    <button
                      onClick={() => setEditingMetric(cfg)}
                      className="flex items-center gap-1 text-[11px] font-medium cursor-pointer transition-colors whitespace-nowrap px-2 py-1 rounded-md hover:bg-gray-50"
                      style={{ color: "#830051" }}
                    >
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-edit-line text-xs"></i>
                      </span>
                      {t("common.edit")}
                    </button>
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    {cfg.timeWindowMinutes}
                    <span className="text-xs font-normal text-gray-400 ml-0.5">
                      {t("anomaly.threshold.minutes")}
                    </span>
                    <span className="text-xs font-normal text-gray-300 mx-1">/</span>
                    {cfg.countThreshold}
                    <span className="text-xs font-normal text-gray-400 ml-0.5">
                      {t("anomaly.threshold.times")}
                    </span>
                  </div>
                  {cfg.metricType === "frequency" && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      {t("anomaly.threshold.frequencyDesc", {
                        minutes: cfg.timeWindowMinutes,
                        count: cfg.countThreshold,
                      })}
                    </p>
                  )}
                  {cfg.metricType === "duplicate" && (
                    <p className="text-[10px] text-gray-400 mt-1">
                      {t("anomaly.threshold.duplicateDesc", {
                        minutes: cfg.timeWindowMinutes,
                        count: cfg.countThreshold,
                      })}
                    </p>
                  )}
                </div>
              );
            })}
      </div>

      {editingMetric && (
        <ThresholdEditModal
          threshold={editingMetric}
          onClose={() => setEditingMetric(null)}
          onSaved={fetchThresholds}
        />
      )}
    </div>
  );
}